import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { RiTimerLine, RiArrowRightLine, RiLoader4Line } from 'react-icons/ri';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../config';

const NewsSection = () => {
    const [newsItems, setNewsItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchNews = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/news/read.php`);
                const data = await response.json();
                if (response.ok && data.records) {
                    setNewsItems(data.records);
                }
            } catch (error) {
                console.error('Error fetching news:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchNews();
    }, []);

    const formatRelativeTime = (dateString) => {
        const now = new Date();
        const past = new Date(dateString);
        const diffInMs = now - past;
        const diffInMins = Math.floor(diffInMs / (1000 * 60));
        const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
        const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

        if (diffInMins < 60) return `${diffInMins} mins ago`;
        if (diffInHours < 24) return `${diffInHours} hours ago`;
        if (diffInDays === 1) return 'Yesterday';
        if (diffInDays < 7) return `${diffInDays} days ago`;
        return past.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const featuredNews = newsItems[0];
    const stackNews = newsItems.slice(1, 4);

    if (loading) {
        return (
            <div className="py-24 bg-white flex justify-center items-center">
                <RiLoader4Line className="animate-spin text-emerald-500 text-3xl" />
            </div>
        );
    }

    if (newsItems.length === 0) return null;

    return (
        <section className="py-24 bg-white">
            <div className="mx-auto max-w-7xl px-6 lg:px-8">
                <div className="flex flex-col md:flex-row justify-between items-end gap-6 mb-12">
                    <div className="max-w-2xl">
                        <motion.span
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            className="text-[10px] font-black text-emerald-600 uppercase tracking-[0.4em]"
                        >
                            Environmental Feed
                        </motion.span>
                        <motion.h2
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.1 }}
                            className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 leading-[1.2] sm:leading-tight font-display"
                        >
                            Latest News <span className="text-emerald-600 italic">Archives.</span>
                        </motion.h2>
                    </div>
                    <Link to="/news">
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] text-emerald-700 hover:text-emerald-900 transition-colors group"
                        >
                            View All Stories
                            <RiArrowRightLine className="group-hover:translate-x-1 transition-transform" />
                        </motion.button>
                    </Link>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                    {/* Featured News */}
                    {featuredNews && (
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            className="lg:col-span-7"
                        >
                            <Link to="/news" className="group block">
                                <div className="relative aspect-[16/9] overflow-hidden rounded-3xl mb-6">
                                    <img
                                        src={featuredNews.image_url || "https://images.unsplash.com/photo-1473448912268-2022ce9509d8?q=80&w=2041&auto=format&fit=crop"}
                                        alt={featuredNews.title}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000"
                                    />
                                    <div className="absolute top-6 left-6 px-4 py-1.5 bg-emerald-600 text-white text-[10px] font-black uppercase tracking-widest rounded-full">
                                        Featured Update
                                    </div>
                                </div>
                                <div className="px-2">
                                    <div className="flex items-center gap-2 text-zinc-400 text-[10px] font-bold mb-3">
                                        <RiTimerLine size={12} className="text-emerald-500" />
                                        <span>{formatRelativeTime(featuredNews.created_at)}</span>
                                    </div>
                                    <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4 group-hover:text-emerald-700 transition-colors leading-tight line-clamp-2 font-display">
                                        {featuredNews.title}
                                    </h3>
                                    <p className="text-sm text-zinc-500 font-medium leading-relaxed mb-6 max-w-2xl line-clamp-3 font-sans">
                                        {featuredNews.content}
                                    </p>
                                    <span className="flex items-center gap-3 text-sm font-black uppercase tracking-widest text-emerald-600 group/btn">
                                        Read Full Story
                                        <RiArrowRightLine className="group-hover/btn:translate-x-1 transition-transform" />
                                    </span>
                                </div>
                            </Link>
                        </motion.div>
                    )}

                    {/* News Stack */}
                    <div className="lg:col-span-5 flex flex-col gap-8">
                        {stackNews.map((item, index) => (
                            <motion.div
                                key={item.id}
                                initial={{ opacity: 0, x: 30 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                            >
                                <Link to="/news" className="group flex gap-6 items-center p-4 rounded-[28px] hover:bg-zinc-50 transition-colors">
                                    <div className="relative w-32 h-24 flex-shrink-0 overflow-hidden rounded-2xl">
                                        <img
                                            src={item.image_url || "https://images.unsplash.com/photo-1544928147-79a2dbc1f389?q=80&w=1974&auto=format&fit=crop"}
                                            alt={item.title}
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                        />
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className="text-[9px] font-black text-emerald-600 uppercase tracking-widest bg-emerald-50 px-2 py-0.5 rounded">
                                                {item.tag}
                                            </span>
                                            <span className="text-[10px] text-zinc-400 font-bold">{formatRelativeTime(item.created_at)}</span>
                                        </div>
                                        <h4 className="font-bold text-slate-900 leading-tight group-hover:text-emerald-700 transition-colors line-clamp-2 font-display text-sm sm:text-base">
                                            {item.title}
                                        </h4>
                                    </div>
                                </Link>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default NewsSection;
